// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
const gamiTargetGph = 0.5;
const gamiWatchGph = 0.7;
const gamiReviewGph = 1.0;

function finite(value) {
  // Treat null/undefined/blank as non-finite. Number(null) and Number("") are 0,
  // so coercing first would turn an explicit null/"" GAMI field (saved JSON / trend
  // rows) into a real 0 — polluting spread bands, peak medians and aggregate stats.
  // Numeric strings like "11.5" still parse.
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function compactText(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function round(value, digits = 2) {
  return Number.isFinite(value) ? Number(value.toFixed(digits)) : null;
}

function average(values) {
  const finiteValues = values.filter(Number.isFinite);
  return finiteValues.length
    ? finiteValues.reduce((total, value) => total + value, 0) / finiteValues.length
    : null;
}

function median(values) {
  const sorted = values.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function sourceCylinderFromPeak(peak) {
  const source = finite(peak?.sourceCylinder ?? peak?.sourceNumber);
  if (Number.isFinite(source) && source > 0) return source;
  const cylinder = finite(peak?.cylinder);
  if (Number.isFinite(cylinder) && cylinder > 0) return cylinder;
  const keyMatch = String(peak?.key ?? "").match(/^(?:egt|cht)(\d+)$/i);
  return keyMatch ? Number(keyMatch[1]) : null;
}

function spreadBand(spreadGph) {
  if (!Number.isFinite(spreadGph)) return "unknown";
  if (spreadGph <= gamiTargetGph) return "target";
  if (spreadGph <= gamiWatchGph) return "watch";
  if (spreadGph <= gamiReviewGph) return "review";
  return "wide";
}

function confidenceRank(confidence) {
  return { high: 2, medium: 1, low: 0 }[String(confidence ?? "").trim().toLowerCase()] ?? 0;
}

function engineOrder(engineId) {
  return {
    engine1: 0,
    left: 0,
    front: 0,
    right: 1,
    rear: 1,
    unknown: 99,
  }[String(engineId ?? "").toLowerCase()] ?? 50;
}

function enginePrefix(engineLabel) {
  const label = compactText(engineLabel).replace(/\s+Engine$/i, "");
  return label && !/^engine$/i.test(label) ? `${label} ` : "";
}

function normalizePeakRows(peaks = [], engine = {}) {
  const rawPeaks = (Array.isArray(peaks) ? peaks : [])
    .map((peak) => {
      const sourceCylinder = sourceCylinderFromPeak(peak);
      return {
        key: compactText(peak?.key),
        sourceCylinder,
        cylinder: finite(peak?.cylinder),
        peakFuelFlowGph: finite(peak?.peakFuelFlowGph),
        peakEgtF: finite(peak?.peakEgtF),
        peakElapsedSeconds: finite(peak?.peakElapsedSeconds),
        clear: peak?.clear === true,
        reason: compactText(peak?.reason),
      };
    })
    .filter((peak) => Number.isFinite(peak.sourceCylinder) || Number.isFinite(peak.cylinder) || peak.key);

  const sourceNumbers = [...new Set(rawPeaks.map((peak) => peak.sourceCylinder).filter(Number.isFinite))]
    .sort((a, b) => a - b);
  const sourceToLocal = new Map(sourceNumbers.map((number, index) => [number, index + 1]));
  const shouldRebase = sourceNumbers.length > 1 && Math.min(...sourceNumbers) > 1;
  const prefix = enginePrefix(engine.engineLabel);
  const medianPeakFuelFlow = median(rawPeaks.map((peak) => peak.peakFuelFlowGph));
  const ordered = rawPeaks
    .filter((peak) => Number.isFinite(peak.peakFuelFlowGph))
    .slice()
    .sort((a, b) => b.peakFuelFlowGph - a.peakFuelFlowGph || (a.sourceCylinder ?? 999) - (b.sourceCylinder ?? 999));
  const orderBySource = new Map(ordered.map((peak, index) => [peak.sourceCylinder ?? peak.key, index + 1]));

  return rawPeaks.map((peak) => {
    const displayCylinder = shouldRebase && Number.isFinite(peak.sourceCylinder)
      ? sourceToLocal.get(peak.sourceCylinder)
      : (Number.isFinite(peak.cylinder) ? peak.cylinder : peak.sourceCylinder);
    const label = Number.isFinite(displayCylinder)
      ? `${prefix}#${displayCylinder}`
      : (peak.key || "Cylinder");
    const peakOrder = orderBySource.get(peak.sourceCylinder ?? peak.key) ?? null;
    return {
      ...peak,
      displayCylinder,
      label,
      peakOrder,
      deltaFromMedianGph: Number.isFinite(peak.peakFuelFlowGph) && Number.isFinite(medianPeakFuelFlow)
        ? round(peak.peakFuelFlowGph - medianPeakFuelFlow, 3)
        : null,
    };
  });
}

function normalizeSweepDetail(detail, row, index = 0) {
  const result = detail?.result_json ?? detail?.result ?? detail ?? {};
  const pilot = detail?.pilot_context_json ?? detail?.pilotContext ?? {};
  const engineId = compactText(result.engineId ?? pilot.engineId ?? detail?.engineId ?? row?.gamiSweepEngineId) || "engine1";
  const engineLabel = compactText(result.engineLabel ?? pilot.engineLabel ?? detail?.engineLabel ?? row?.gamiSweepEngineLabel) || "Engine";
  const spreadGph = finite(result.spreadGph ?? detail?.spreadGph ?? row?.gamiSweepSpreadGph);
  const startTime = finite(detail?.start_time ?? detail?.startTime ?? result.startTime ?? row?.gamiSweepStartTime);
  const endTime = finite(detail?.end_time ?? detail?.endTime ?? result.endTime ?? row?.gamiSweepEndTime);
  const clearPeakCount = finite(result.clearPeakCount ?? detail?.clearPeakCount ?? row?.gamiSweepClearPeakCount);
  const peakCount = finite(result.peakCount ?? detail?.peakCount ?? row?.gamiSweepPeakCount);
  const peaks = normalizePeakRows(result.peaks ?? detail?.peaks, { engineId, engineLabel });
  const peaksWithFuel = peaks.filter((peak) => Number.isFinite(peak.peakFuelFlowGph));
  const firstPeak = peaksWithFuel.slice().sort((a, b) => b.peakFuelFlowGph - a.peakFuelFlowGph)[0] ?? null;
  const lastPeak = peaksWithFuel.slice().sort((a, b) => a.peakFuelFlowGph - b.peakFuelFlowGph)[0] ?? null;
  return {
    id: compactText(detail?.id ?? result.id) || `${row?.id ?? "row"}-gami-${index}`,
    rowId: row?.id ?? "",
    flightTitle: row?.title ?? row?.flightTitle ?? row?.id ?? "",
    dateLabel: row?.dateLabel ?? "",
    dateValue: finite(row?.dateValue) ?? 0,
    engineId,
    engineLabel,
    spreadGph,
    spreadBand: compactText(result.spreadBand ?? detail?.spreadBand ?? row?.gamiSweepSpreadBand) || spreadBand(spreadGph),
    score: finite(detail?.score ?? row?.gamiSweepScore),
    confidence: compactText(result.confidence ?? detail?.confidence ?? row?.gamiSweepConfidence) || "",
    status: compactText(result.status ?? detail?.status ?? row?.gamiSweepStatus) || "",
    startTime,
    endTime,
    clearPeakCount,
    peakCount,
    savedAt: compactText(detail?.updated_at ?? detail?.savedAt ?? result.savedAt ?? row?.gamiSweepSavedAt),
    peaks,
    firstPeakLabel: firstPeak?.label ?? "",
    lastPeakLabel: lastPeak?.label ?? "",
  };
}

export function extractGamiSweepDetails(rows = []) {
  return (Array.isArray(rows) ? rows : []).flatMap((row) => {
    const details = Array.isArray(row?.gamiSweepDetails) ? row.gamiSweepDetails : [];
    if (details.length) return details.map((detail, index) => normalizeSweepDetail(detail, row, index));
    if (!Number.isFinite(row?.gamiSweepSpreadGph)) return [];
    return [normalizeSweepDetail({}, row, 0)];
  }).sort((a, b) => {
    const dateDelta = (a.dateValue ?? 0) - (b.dateValue ?? 0);
    if (dateDelta !== 0) return dateDelta;
    const engineDelta = engineOrder(a.engineId) - engineOrder(b.engineId);
    if (engineDelta !== 0) return engineDelta;
    return (a.startTime ?? 0) - (b.startTime ?? 0);
  });
}

function aggregateSweepUsable(sweep) {
  if (!Number.isFinite(sweep?.spreadGph)) return false;
  if (String(sweep.confidence).toLowerCase() === "low") return false;
  if (confidenceRank(sweep.confidence) >= 1) return true;
  const status = String(sweep.status ?? "").toLowerCase();
  return status === "complete" || status === "partial";
}

function buildSpreadTrend(sweeps) {
  if (new Set(sweeps.map((sweep) => sweep.engineId)).size > 1) {
    return { key: "limited", label: "Select an engine", detail: "Compare saved sweeps within the same engine to assess direction.", deltaGph: null };
  }
  const withSpread = sweeps.filter((sweep) => Number.isFinite(sweep.spreadGph));
  if (withSpread.length < 3) {
    return {
      key: "limited",
      label: "Limited history",
      detail: "Need at least three saved sweeps to call a direction.",
      deltaGph: null,
    };
  }
  const windowSize = Math.min(3, Math.floor(withSpread.length / 2));
  const recent = withSpread.slice(-windowSize).map((sweep) => sweep.spreadGph);
  const prior = withSpread.slice(-windowSize * 2, -windowSize).map((sweep) => sweep.spreadGph);
  const recentAverage = average(recent);
  const priorAverage = average(prior);
  const delta = Number.isFinite(recentAverage) && Number.isFinite(priorAverage) ? recentAverage - priorAverage : null;
  const range = Math.max(...withSpread.map((sweep) => sweep.spreadGph)) - Math.min(...withSpread.map((sweep) => sweep.spreadGph));
  if (Number.isFinite(delta) && delta >= 0.1) {
    return {
      key: "widening",
      label: "Spread widening",
      detail: `Recent sweeps average ${round(delta, 2)} GPH wider than the prior set.`,
      deltaGph: round(delta, 2),
    };
  }
  if (Number.isFinite(delta) && delta <= -0.1) {
    return {
      key: "narrowing",
      label: "Spread tightening",
      detail: `Recent sweeps average ${round(Math.abs(delta), 2)} GPH tighter than the prior set.`,
      deltaGph: round(delta, 2),
    };
  }
  if (range >= 0.25) {
    return {
      key: "inconsistent",
      label: "Spread inconsistent",
      detail: `Saved sweeps vary by ${round(range, 2)} GPH, so compare sweep quality before calling the trend stable.`,
      deltaGph: round(delta ?? 0, 2),
    };
  }
  return {
    key: "stable",
    label: "Spread stable",
    detail: "Recent sweeps are staying within a small GPH band.",
    deltaGph: round(delta ?? 0, 2),
  };
}

function buildCylinderSummaries(sweeps) {
  const groups = new Map();
  sweeps.forEach((sweep) => {
    const sweepPeaks = (sweep.peaks ?? []).filter((peak) => Number.isFinite(peak.peakFuelFlowGph));
    const peakCount = sweepPeaks.length;
    if (!peakCount) return;
    sweepPeaks.forEach((peak) => {
      const key = `${sweep.engineId}:${peak.displayCylinder ?? peak.sourceCylinder ?? peak.key}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          engineId: sweep.engineId,
          engineLabel: sweep.engineLabel,
          sourceCylinder: peak.sourceCylinder ?? null,
          displayCylinder: peak.displayCylinder ?? null,
          label: peak.label,
          samples: [],
        });
      }
      groups.get(key).samples.push({
        sweepId: sweep.id,
        dateValue: sweep.dateValue,
        peakFuelFlowGph: peak.peakFuelFlowGph,
        peakEgtF: peak.peakEgtF,
        deltaFromMedianGph: peak.deltaFromMedianGph,
        peakOrder: peak.peakOrder,
        first: peak.peakOrder === 1,
        last: peak.peakOrder === peakCount,
      });
    });
  });

  return [...groups.values()].map((group) => {
    const samples = group.samples.slice().sort((a, b) => a.dateValue - b.dateValue);
    const peakFfs = samples.map((sample) => sample.peakFuelFlowGph).filter(Number.isFinite);
    const deltas = samples.map((sample) => sample.deltaFromMedianGph).filter(Number.isFinite);
    const orders = samples.map((sample) => sample.peakOrder).filter(Number.isFinite);
    const egts = samples.map((sample) => sample.peakEgtF).filter(Number.isFinite);
    const latest = samples.slice().reverse().find((sample) => Number.isFinite(sample.deltaFromMedianGph));
    const priorDeltas = samples
      .filter((sample) => sample !== latest)
      .map((sample) => sample.deltaFromMedianGph)
      .filter(Number.isFinite);
    const priorAverageDelta = average(priorDeltas);
    const latestDelta = latest?.deltaFromMedianGph ?? null;
    const driftGph = Number.isFinite(latestDelta) && Number.isFinite(priorAverageDelta)
      ? latestDelta - priorAverageDelta
      : null;
    const avgDelta = average(deltas);
    const avgAbsDelta = average(deltas.map((delta) => Math.abs(delta)));
    const firstCount = samples.filter((sample) => sample.first).length;
    const lastCount = samples.filter((sample) => sample.last).length;
    const absDrift = Number.isFinite(latestDelta) && Number.isFinite(priorAverageDelta)
      ? Math.abs(latestDelta) - Math.abs(priorAverageDelta)
      : null;
    const direction = Number.isFinite(avgDelta) && avgDelta > 0.05
      ? "early"
      : Number.isFinite(avgDelta) && avgDelta < -0.05 ? "late" : "near";
    const drift = Number.isFinite(driftGph) && driftGph > 0.08
      ? "earlier"
      : Number.isFinite(driftGph) && driftGph < -0.08 ? "later" : "stable";
    return {
      ...group,
      count: samples.length,
      avgPeakFuelFlowGph: round(average(peakFfs), 2),
      minPeakFuelFlowGph: round(Math.min(...peakFfs), 2),
      maxPeakFuelFlowGph: round(Math.max(...peakFfs), 2),
      avgPeakEgtF: round(average(egts), 0),
      avgDeltaGph: round(avgDelta, 3),
      avgAbsDeltaGph: round(avgAbsDelta, 3),
      minDeltaGph: round(Math.min(...deltas), 3),
      maxDeltaGph: round(Math.max(...deltas), 3),
      latestDeltaGph: round(latestDelta, 3),
      priorAverageDeltaGph: round(priorAverageDelta, 3),
      driftGph: round(driftGph, 3),
      absDriftGph: round(absDrift, 3),
      avgPeakOrder: round(average(orders), 1),
      firstCount,
      lastCount,
      direction,
      drift,
    };
  }).sort((a, b) => {
    const engineDelta = engineOrder(a.engineId) - engineOrder(b.engineId);
    if (engineDelta !== 0) return engineDelta;
    const deltaA = Number.isFinite(a.avgDeltaGph) ? a.avgDeltaGph : -999;
    const deltaB = Number.isFinite(b.avgDeltaGph) ? b.avgDeltaGph : -999;
    return deltaB - deltaA || (a.displayCylinder ?? 999) - (b.displayCylinder ?? 999);
  });
}

function driverSummary(cylinderSummaries) {
  const driver = cylinderSummaries
    .filter((summary) => Number.isFinite(summary.avgAbsDeltaGph))
    .slice()
    .sort((a, b) => {
      const aWeight = a.avgAbsDeltaGph + (a.firstCount + a.lastCount) / Math.max(1, a.count) * 0.08 + Math.max(0, a.absDriftGph ?? 0) * 0.4;
      const bWeight = b.avgAbsDeltaGph + (b.firstCount + b.lastCount) / Math.max(1, b.count) * 0.08 + Math.max(0, b.absDriftGph ?? 0) * 0.4;
      return bWeight - aWeight || String(a.label).localeCompare(String(b.label));
    })[0] ?? null;
  if (!driver) return { cylinder: null, label: "Cylinder driver unavailable", detail: "Saved sweeps do not include enough peak fuel-flow detail." };
  const side = driver.direction === "early"
    ? "early/lean-side"
    : driver.direction === "late" ? "late/rich-side" : "near-median";
  const drift = driver.drift === "earlier"
    ? " and is moving earlier recently"
    : driver.drift === "later" ? " and is moving later recently" : "";
  return {
    cylinder: driver,
    label: `${driver.label} is the main ${side} driver`,
    detail: `${driver.label} averages ${formatGph(driver.avgDeltaGph, { signed: true })} from the sweep median${drift}.`,
  };
}

export function formatGph(value, { signed = false, digits = 2 } = {}) {
  if (!Number.isFinite(value)) return "--";
  const prefix = signed && value > 0 ? "+" : "";
  return `${prefix}${Number(value).toFixed(digits)} GPH`;
}

export function buildGamiTrendAggregate(rows = []) {
  const sweeps = extractGamiSweepDetails(rows);
  const chartSweeps = sweeps.filter((sweep) => Number.isFinite(sweep.spreadGph));
  const usableSweeps = chartSweeps.filter(aggregateSweepUsable);
  const aggregateSweeps = usableSweeps;
  const spreads = aggregateSweeps.map((sweep) => sweep.spreadGph).filter(Number.isFinite);
  const latest = chartSweeps.at(-1) ?? null;
  const cylinderSummaries = buildCylinderSummaries(aggregateSweeps);
  const spreadAverage = average(spreads);
  const spreadMin = spreads.length ? Math.min(...spreads) : null;
  const spreadMax = spreads.length ? Math.max(...spreads) : null;
  const trend = buildSpreadTrend(aggregateSweeps);
  const driver = driverSummary(cylinderSummaries);
  return {
    sweeps,
    chartSweeps,
    usableSweeps,
    aggregateSweeps,
    cylinderSummaries,
    driver,
    trend,
    stats: {
      totalSweeps: sweeps.length,
      chartSweepCount: chartSweeps.length,
      aggregateSweepCount: aggregateSweeps.length,
      usableSweepCount: usableSweeps.length,
      lowConfidenceCount: chartSweeps.filter((sweep) => confidenceRank(sweep.confidence) === 0).length,
      partialCount: chartSweeps.filter((sweep) => /partial/i.test(sweep.status)).length,
      flightsWithSweeps: new Set(sweeps.map((sweep) => sweep.rowId).filter(Boolean)).size,
      spreadAverageGph: round(spreadAverage, 2),
      spreadMedianGph: round(median(spreads), 2),
      spreadMinGph: round(spreadMin, 2),
      spreadMaxGph: round(spreadMax, 2),
      latestSpreadGph: round(latest?.spreadGph, 2),
      latestLabel: latest?.dateLabel ?? "",
      aggregateSpreadBand: spreadBand(spreadAverage),
    },
  };
}
